import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaTrash }        from '@react-icons/all-files/fa/FaTrash';
import { FaEnvelope }     from '@react-icons/all-files/fa/FaEnvelope';
import { FaEnvelopeOpen } from '@react-icons/all-files/fa/FaEnvelopeOpen';
import { FaInbox }        from '@react-icons/all-files/fa/FaInbox';
import { FaSync }         from '@react-icons/all-files/fa/FaSync';
import { FaTasks }        from '@react-icons/all-files/fa/FaTasks';
import { FaChevronLeft }  from '@react-icons/all-files/fa/FaChevronLeft';
import { FaChevronRight } from '@react-icons/all-files/fa/FaChevronRight';
import { FaChevronDown }  from '@react-icons/all-files/fa/FaChevronDown';
import { FaChevronUp }    from '@react-icons/all-files/fa/FaChevronUp';
import { FaColumns }         from '@react-icons/all-files/fa/FaColumns';
import { FaExternalLinkAlt } from '@react-icons/all-files/fa/FaExternalLinkAlt';
import { FaEnvelopeOpenText } from '@react-icons/all-files/fa/FaEnvelopeOpenText';
import { FaHistory }          from '@react-icons/all-files/fa/FaHistory';
import { FaTag }              from '@react-icons/all-files/fa/FaTag';
import { FaExclamationCircle } from '@react-icons/all-files/fa/FaExclamationCircle';
import { FaUser }             from '@react-icons/all-files/fa/FaUser';
import { FaSearch }           from '@react-icons/all-files/fa/FaSearch';

const PAGE_SIZE = 20;
const API_BASE  = '/api/profase-usuario-inboxes/me';

// ── Helpers ───────────────────────────────────────────────────────────────────

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('jhi-authenticationToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmtDate(dateStr?: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (isToday) {
    return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
}

// ── Tag chip ─────────────────────────────────────────────────────────────────

function TagChip({ label, color }: { label: string; color?: string | null }) {
  const bg = color && color !== 'yellow' && color !== 'green' ? color : (
    color === 'yellow' ? '#fef3c7' :
    color === 'green'  ? '#d1fae5' : '#e0e7ff'
  );
  const text = color === 'yellow' ? '#92400e' :
               color === 'green'  ? '#065f46' :
               color === '#ff0000' || color === 'red' ? '#fff' : '#1e40af';
  const border = color === '#ff0000' || color === 'red' ? 'none' : '1px solid rgba(0,0,0,0.08)';
  const finalBg = (color === '#ff0000' || color === 'red') ? '#ef4444' : bg;

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '1px 8px', borderRadius: 12,
      fontSize: 11, fontWeight: 600,
      background: finalBg, color: text, border,
      whiteSpace: 'nowrap' as const,
    }}>
      {label}
    </span>
  );
}

// ── Avatar initials ───────────────────────────────────────────────────────────

function Avatar({ name, size = 32 }: { name?: string | null; size?: number }) {
  const initials = name
    ? name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : '?';
  const colors = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
  const idx = (name?.charCodeAt(0) ?? 0) % colors.length;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: colors[idx], color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.38), fontWeight: 700,
    }}>
      {initials}
    </div>
  );
}

// ── DeleteConfirmBanner ───────────────────────────────────────────────────────

function DeleteConfirmBanner({
  count,
  onConfirm,
  onCancel,
}: {
  count: number;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div style={dc.banner}>
      <FaTrash style={{ fontSize: 14, color: '#dc2626', flexShrink: 0 }} />
      <span style={dc.text}>
        ¿Eliminar <strong>{count} {count === 1 ? 'mensaje' : 'mensajes'}</strong> de la bandeja?
      </span>
      <div style={dc.actions}>
        <button style={dc.btnCancel} onClick={onCancel}>Cancelar</button>
        <button style={dc.btnDelete} onClick={onConfirm}>
          <FaTrash style={{ fontSize: 11, marginRight: 5 }} />
          Eliminar
        </button>
      </div>
    </div>
  );
}

const dc: Record<string, React.CSSProperties> = {
  banner: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 16px',
    background: '#fef2f2',
    borderBottom: '1px solid #fecaca',
    flexWrap: 'wrap' as const,
  },
  text: {
    flex: 1, fontSize: 13, color: '#991b1b',
  },
  actions: {
    display: 'flex', gap: 8,
  },
  btnCancel: {
    padding: '6px 16px', background: '#fff', color: '#374151',
    border: '1px solid #d1d5db', borderRadius: 7,
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  btnDelete: {
    display: 'inline-flex', alignItems: 'center',
    padding: '6px 16px', background: '#dc2626', color: '#fff',
    border: 'none', borderRadius: 7,
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
};

// ── BodyMailComponent ─────────────────────────────────────────────────────────

interface BodyItem {
  type: 'h1' | 'h2' | 'h3' | 'p' | 'button' | 'kanban' | string;
  text?: string | null;
  params?: any;
}

export function BodyMailComponent({ items }: { items: BodyItem[] }) {
  return (
    <div style={bm.root}>
      {items.map((item, i) => {
        switch (item.type) {
          case 'h1':
            return <h1 key={i} style={bm.h1}>{item.text}</h1>;
          case 'h2':
            return <h2 key={i} style={bm.h2}>{item.text}</h2>;
          case 'h3':
            return <h3 key={i} style={bm.h3}>{item.text}</h3>;
          case 'p':
            return <p key={i} style={bm.p}>{item.text}</p>;
          case 'button':
            return (
              <button
                key={i}
                style={bm.button}
                onClick={() => item.params?.url && window.open(item.params.url, '_blank')}
              >
                <FaExternalLinkAlt style={{ fontSize: 12, marginRight: 6 }} />
                {item.text ?? 'Abrir'}
              </button>
            );
          case 'kanban':
            return (
              <button
                key={i}
                style={bm.kanbanBtn}
                onClick={() => window.location.hash = '/kanban'}
              >
                <FaColumns style={{ fontSize: 13, marginRight: 6 }} />
                {item.text ?? 'Vista Kanban'}
              </button>
            );
          default:
            return item.text
              ? <p key={i} style={bm.p}>{item.text}</p>
              : null;
        }
      })}
    </div>
  );
}

const bm: Record<string, React.CSSProperties> = {
  root: { display: 'flex', flexDirection: 'column', gap: 10 },
  h1:   { margin: 0, fontSize: 20, fontWeight: 700, color: '#202124', lineHeight: 1.3 },
  h2:   { margin: 0, fontSize: 16, fontWeight: 700, color: '#202124', lineHeight: 1.3 },
  h3:   { margin: 0, fontSize: 14, fontWeight: 700, color: '#202124', lineHeight: 1.3 },
  p:    { margin: 0, fontSize: 14, color: '#3c4043', lineHeight: 1.6 },
  button: {
    display: 'inline-flex', alignItems: 'center',
    padding: '9px 20px', background: '#6366f1', color: '#fff',
    border: 'none', borderRadius: 8,
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    alignSelf: 'flex-start',
  },
  kanbanBtn: {
    display: 'inline-flex', alignItems: 'center',
    padding: '9px 20px', background: '#0ea5e9', color: '#fff',
    border: 'none', borderRadius: 8,
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    alignSelf: 'flex-start',
  },
};

// ── Main component ────────────────────────────────────────────────────────────

export const Inbox = () => {
  const [items,         setItems]         = useState<any[]>([]);
  const [totalItems,    setTotalItems]    = useState(0);
  const [page,          setPage]          = useState(0);   // 0-indexed
  const [loading,       setLoading]       = useState(false);
  const [selected,      setSelected]      = useState<Set<number>>(new Set());
  const [expanded,      setExpanded]      = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number[] | null>(null);
  const [newItemIds,    setNewItemIds]    = useState<Set<number>>(new Set());
  // 'inbox' | 'noLeidos' | 'historico' | 'tag:Urgente' | 'tag:Cliente' | 'tag:Revisar Bien'
  const [view,          setView]          = useState<string>('inbox');
  const [tagsOpen,      setTagsOpen]      = useState(true);
  // Cache of fetched detail data keyed by item id
  const [detailCache,   setDetailCache]   = useState<Record<number, any>>({});
  const [detailLoading, setDetailLoading] = useState<number | null>(null);
  const topRef    = useRef<HTMLDivElement>(null);
  const knownIds  = useRef<Set<number>>(new Set());

  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  // ── Fetch ───────────────────────────────────────────────────────────────────
  // ── Map view → extra API query params ────────────────────────────────────
  const viewParams = useCallback((v: string): string => {
    if (v === 'noLeidos')    return '&leido=false';
    if (v === 'leidos')      return '&leido=true';
    if (v === 'historico')   return '&historico=true';
    if (v === 'actividades') return '&actividad=true';
    if (v.startsWith('tag:')) {
      const tagName = v.slice(4).toLowerCase().replace(/\s+/g, '');
      return `&tagName=${encodeURIComponent(tagName)}&tagValue=true`;
    }
    return '';
  }, []);

  const fetchPage = useCallback(async (p: number, highlightNew = false, forView?: string) => {
    const activeView = forView ?? view;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}?page=${p}&size=${PAGE_SIZE}&sort=id,desc${viewParams(activeView)}&cacheBuster=${Date.now()}`,
        { headers: { ...authHeader() } },
      );
      if (res.ok) {
        const total = parseInt(res.headers.get('x-total-count') ?? '0', 10);
        setTotalItems(total);
        const data: any[] = await res.json();
        setItems(data);

        if (highlightNew) {
          const incoming = data.map((i: any) => i.id as number);
          const fresh = new Set(incoming.filter(id => !knownIds.current.has(id)));
          if (fresh.size > 0) {
            setNewItemIds(fresh);
            setTimeout(() => setNewItemIds(new Set()), 2500);
          }
        }
        data.forEach((i: any) => knownIds.current.add(i.id));
      }
    } finally {
      setLoading(false);
    }
  }, [view, viewParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchPage(page);
    setSelected(new Set());
    setExpanded(null);
    topRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page, fetchPage]);

  // ── Listen for Firebase push → refresh inbox ─────────────────────────────
  useEffect(() => {
    const handler = () => {
      if (page === 0) {
        fetchPage(0, true);
      } else {
        setPage(0);
      }
    };
    window.addEventListener('profase:inbox:refresh', handler);
    return () => window.removeEventListener('profase:inbox:refresh', handler);
  }, [page, fetchPage]);

  // ── Selection helpers ────────────────────────────────────────────────────────
  const allSelected = items.length > 0 && items.every(i => selected.has(i.id));

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(items.map(i => i.id)));
  };

  const toggleOne = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Detail fetch ────────────────────────────────────────────────────────────
  const fetchDetail = useCallback(async (id: number) => {
    if (detailCache[id]) return; // already loaded
    setDetailLoading(id);
    try {
      const res = await fetch(`${API_BASE}/${id}/data`, { headers: { ...authHeader() } });
      if (res.ok) {
        const data = await res.json();
        setDetailCache(prev => ({ ...prev, [id]: data }));
        // Mark as read locally
        setItems(prev => prev.map(i => i.id === id ? { ...i, leido: true } : i));
      }
    } finally {
      setDetailLoading(null);
    }
  }, [detailCache]);

  const handleExpand = (id: number) => {
    const next = expanded === id ? null : id;
    setExpanded(next);
    if (next !== null) fetchDetail(next);
  };

  // ── Actions (optimistic UI, no separate endpoint yet) ────────────────────────
  const markRead = async (ids: number[], read: boolean) => {
    // Optimistic update
    setItems(prev => prev.map(i => ids.includes(i.id) ? { ...i, leido: read } : i));

    if (!read) {
      // Call no-leido endpoint for each selected item
      await Promise.allSettled(
        ids.map(id =>
          fetch(`${API_BASE}/${id}/no-leido`, {
            method: 'PUT',
            headers: { ...authHeader() },
          })
        )
      );
    }
  };

  const deleteItems = async (ids: number[]) => {
    // Optimistic remove
    setItems(prev => prev.filter(i => !ids.includes(i.id)));
    setTotalItems(prev => prev - ids.length);
    setSelected(new Set());

    // Fire DELETE requests in parallel; silently ignore individual failures
    await Promise.allSettled(
      ids.map(id =>
        fetch(`${API_BASE}/${id}`, {
          method: 'DELETE',
          headers: { ...authHeader() },
        })
      )
    );
  };

  const selectedIds = Array.from(selected);
  const unreadCount = items.filter(i => !i.leido).length;

  // Server already filters by view params; items are the correct set
  const visibleItems = items;

  const handleViewChange = (v: string) => {
    setView(v);
    setPage(0);
    setExpanded(null);
    setSelected(new Set());
    // Always fetch immediately with the new view's params
    fetchPage(0, false, v);
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={s.root}>
      <style>{`
        @keyframes inboxSlideIn {
          0%   { opacity: 0; transform: translateY(-12px); background: #e0e7ff; }
          30%  { opacity: 1; transform: translateY(0);     background: #e0e7ff; }
          100% { background: transparent; }
        }
        .inbox-new-item {
          animation: inboxSlideIn 2.2s ease forwards;
        }
      `}</style>

      {/* ── Sidebar ── */}
      <aside style={s.sidebar}>

        <SideItem icon={<FaInbox />}            label="Bandeja de entrada" active={view === 'inbox'}     onClick={() => handleViewChange('inbox')}     badge={unreadCount || undefined} />
        <SideItem icon={<FaEnvelopeOpenText />}  label="No Leídos"          active={view === 'noLeidos'}  onClick={() => handleViewChange('noLeidos')}  badge={unreadCount || undefined} />
        <SideItem icon={<FaEnvelopeOpen />}      label="Leídos"             active={view === 'leidos'}    onClick={() => handleViewChange('leidos')} />
        <SideItem icon={<FaHistory />}           label="Histórico"          active={view === 'historico'} onClick={() => handleViewChange('historico')} />
        <SideItem icon={<FaTasks />}             label="Actividades"        active={view === 'actividades'} onClick={() => handleViewChange('actividades')} />

        <div style={s.sideSection}>
          <div style={s.sideSectionHeader} onClick={() => setTagsOpen(o => !o)}>
            <FaTag style={{ fontSize: 13, color: '#5f6368' }} />
            <span style={{ flex: 1 }}>Etiquetas</span>
            {tagsOpen
              ? <FaChevronUp  style={{ fontSize: 10, color: '#80868b' }} />
              : <FaChevronDown style={{ fontSize: 10, color: '#80868b' }} />
            }
          </div>
          {tagsOpen && (
            <div style={{ paddingLeft: 8 }}>
              <SideItem icon={<FaExclamationCircle style={{ color: '#ef4444' }} />} label="Urgente"      active={view === 'tag:Urgente'}      onClick={() => handleViewChange('tag:Urgente')}      indent />
              <SideItem icon={<FaUser style={{ color: '#3b82f6' }} />}              label="Cliente"      active={view === 'tag:Cliente'}      onClick={() => handleViewChange('tag:Cliente')}      indent />
              <SideItem icon={<FaSearch style={{ color: '#10b981' }} />}            label="Revisar Bien" active={view === 'tag:Revisar Bien'} onClick={() => handleViewChange('tag:Revisar Bien')} indent />
            </div>
          )}
        </div>

      </aside>

      {/* ── Main panel ── */}
      <main style={s.main}>

        {/* Toolbar */}
        <div style={s.toolbar}>
          <div style={s.toolbarLeft}>
            <label style={s.checkWrapper}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                style={s.check}
              />
              <FaChevronDown style={{ fontSize: 10, color: '#5f6368', marginLeft: 2 }} />
            </label>

            <ToolBtn title="Actualizar" onClick={() => fetchPage(page)}>
              <FaSync style={{ fontSize: 14, color: loading ? '#6366f1' : '#5f6368' }} />
            </ToolBtn>

            {selectedIds.length > 0 && (
              <>
                <ToolBtn title="Marcar como leído" onClick={() => markRead(selectedIds, true)}>
                  <FaEnvelopeOpen style={{ fontSize: 14, color: '#5f6368' }} />
                </ToolBtn>
                <ToolBtn title="Marcar como no leído" onClick={() => markRead(selectedIds, false)}>
                  <FaEnvelope style={{ fontSize: 14, color: '#5f6368' }} />
                </ToolBtn>
                <ToolBtn title="Eliminar" onClick={() => setConfirmDelete(selectedIds)}>
                  <FaTrash style={{ fontSize: 14, color: '#5f6368' }} />
                </ToolBtn>
              </>
            )}
          </div>

          {/* Pagination info */}
          <div style={s.toolbarRight}>
            <span style={s.pageInfo}>
              {totalItems === 0 ? '0' : `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, totalItems)}`} de {totalItems}
            </span>
            <ToolBtn title="Página anterior" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
              <FaChevronLeft style={{ fontSize: 13, color: page === 0 ? '#dadce0' : '#5f6368' }} />
            </ToolBtn>
            <ToolBtn title="Página siguiente" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>
              <FaChevronRight style={{ fontSize: 13, color: page >= totalPages - 1 ? '#dadce0' : '#5f6368' }} />
            </ToolBtn>
          </div>
        </div>

        <div style={s.divider} />

        {/* Delete confirmation banner */}
        {confirmDelete && (
          <DeleteConfirmBanner
            count={confirmDelete.length}
            onConfirm={() => { deleteItems(confirmDelete); setConfirmDelete(null); }}
            onCancel={() => setConfirmDelete(null)}
          />
        )}

        {/* Loading overlay */}
        {loading && <div style={s.loadingBar} />}

        {/* Item list */}
        <div style={s.list} ref={topRef}>
          {visibleItems.length === 0 && !loading && (
            <div style={s.empty}>
              <FaInbox style={{ fontSize: 48, color: '#dadce0', marginBottom: 12 }} />
              <span style={{ color: '#5f6368', fontSize: 15 }}>No hay mensajes</span>
            </div>
          )}

          {visibleItems.map(item => (
            <div
              key={item.id}
              className={newItemIds.has(item.id) ? 'inbox-new-item' : undefined}
              style={s.row(item.leido, selected.has(item.id))}
            >

              {/* Checkbox */}
              <div style={s.rowLeft} onClick={e => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selected.has(item.id)}
                  onChange={() => toggleOne(item.id)}
                  style={s.check}
                />
              </div>

              {/* Avatar */}
              <div style={{ flexShrink: 0 }} onClick={() => { toggleOne(item.id); }}>
                <Avatar name={item.remitente ?? item.asunto} />
              </div>

              {/* Content */}
              <div style={s.rowContent} onClick={() => handleExpand(item.id)}>

                {/* Top line: sender + subject + tags + date */}
                <div style={s.rowTop}>
                  <span style={s.sender(!item.leido)}>
                    {item.remitente ?? 'Sistema'}
                  </span>

                  <span style={s.subject(!item.leido)}>
                    {item.asunto ?? '(sin asunto)'}
                  </span>

                  {/* Tags */}
                  {(item.tags ?? []).length > 0 && (
                    <div style={s.tags}>
                      {(item.tags as any[]).map((t: any, i: number) => (
                        <TagChip key={i} label={t.label} color={t.color} />
                      ))}
                    </div>
                  )}

                  <span style={s.date(!item.leido)}>{fmtDate(item.fecha)}</span>
                </div>

                {/* Snippet */}
                {item.mensaje && (
                  <div style={s.snippet}>{item.mensaje}</div>
                )}

                {/* Type badge row */}
                <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                  {item.actividad && (
                    <span style={s.typeBadge('#ede9fe', '#6d28d9')}>
                      <FaTasks style={{ fontSize: 9 }} /> Actividad
                    </span>
                  )}
                </div>

                {/* Expanded detail body */}
                {expanded === item.id && (
                  <div style={s.expandedBody}>
                    {detailLoading === item.id ? (
                      <div style={s.detailLoading}>
                        <div style={s.spinner} />
                        <span style={{ fontSize: 13, color: '#80868b' }}>Cargando…</span>
                      </div>
                    ) : detailCache[item.id] ? (
                      <MailDetailView detail={detailCache[item.id]} />
                    ) : (
                      <p style={{ margin: 0, fontSize: 13, color: '#80868b' }}>(sin contenido)</p>
                    )}
                  </div>
                )}
              </div>

              {/* Expand toggle */}
              <div style={s.expandBtn} onClick={() => handleExpand(item.id)}>
                {expanded === item.id
                  ? <FaChevronUp  style={{ fontSize: 11, color: '#80868b' }} />
                  : <FaChevronDown style={{ fontSize: 11, color: '#80868b' }} />
                }
              </div>

            </div>
          ))}
        </div>

        {/* Bottom pagination */}
        {totalItems > PAGE_SIZE && (
          <div style={s.bottomPager}>
            <span style={s.pageInfo}>
              Página {page + 1} de {totalPages}
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              <PagerBtn label="‹ Anterior" onClick={() => setPage(p => p - 1)} disabled={page === 0} />
              {Array.from({ length: totalPages }, (_, i) => (
                <PagerBtn key={i} label={String(i + 1)} onClick={() => setPage(i)} active={i === page} />
              ))}
              <PagerBtn label="Siguiente ›" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1} />
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

// ── SideItem ─────────────────────────────────────────────────────────────────

function SideItem({ icon, label, active, onClick, badge, indent }: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  badge?: number;
  indent?: boolean;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: indent ? '6px 16px 6px 16px' : '8px 16px 8px 24px',
        borderRadius: '0 24px 24px 0',
        fontSize: indent ? 13 : 14,
        fontWeight: active ? 700 : 400,
        color: active ? '#202124' : '#444746',
        background: active ? '#d3e3fd' : 'transparent',
        cursor: 'pointer',
        userSelect: 'none' as const,
        transition: 'background 0.1s',
      }}
      onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.background = '#f1f3f4'; }}
      onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
    >
      <span style={{ fontSize: indent ? 13 : 16, flexShrink: 0 }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {badge !== undefined && badge > 0 && (
        <span style={{ fontSize: 11, fontWeight: 700, color: active ? '#1a73e8' : '#202124' }}>
          {badge}
        </span>
      )}
    </div>
  );
}

// ── MailDetailView ────────────────────────────────────────────────────────────

function MailDetailView({ detail }: { detail: any }) {
  const remitente = detail.remitente;
  const bodyItems: BodyItem[] = detail.body?.items ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Header: remitente + fecha */}
      <div style={md.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={remitente?.nombre ?? 'Sistema'} size={36} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={md.fromName}>{remitente?.nombre ?? 'Sistema'}</span>
            {remitente?.email && (
              <span style={md.fromEmail}>&lt;{remitente.email}&gt;</span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <span style={md.date}>{detail.fecha ?? ''}</span>
          {detail.urgente && (
            <span style={md.urgenteBadge}>🔴 Urgente</span>
          )}
        </div>
      </div>

      {/* Subject */}
      <h2 style={md.subject}>{detail.asunto}</h2>

      <div style={md.divider} />

      {/* Body rendered by type */}
      {bodyItems.length > 0
        ? <BodyMailComponent items={bodyItems} />
        : <p style={{ margin: 0, fontSize: 14, color: '#5f6368' }}>(sin contenido)</p>
      }
    </div>
  );
}

const md: Record<string, React.CSSProperties> = {
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 },
  fromName:    { fontSize: 14, fontWeight: 700, color: '#202124' },
  fromEmail:   { fontSize: 12, color: '#80868b' },
  date:        { fontSize: 12, color: '#80868b', whiteSpace: 'nowrap' },
  urgenteBadge:{ fontSize: 11, fontWeight: 700, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '2px 8px' },
  subject:     { margin: 0, fontSize: 18, fontWeight: 700, color: '#202124' },
  divider:     { height: 1, background: '#f1f3f4' },
};

// ── Micro-components ──────────────────────────────────────────────────────────

function ToolBtn({ children, title, onClick, disabled }: {
  children: React.ReactNode; title?: string; onClick?: () => void; disabled?: boolean;
}) {
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        all: 'unset', cursor: disabled ? 'default' : 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 32, height: 32, borderRadius: '50%',
        transition: 'background 0.12s',
      }}
      onMouseEnter={e => { if (!disabled) (e.currentTarget as HTMLElement).style.background = '#f1f3f4'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
    >
      {children}
    </button>
  );
}

function PagerBtn({ label, onClick, disabled, active }: {
  label: string; onClick: () => void; disabled?: boolean; active?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '5px 10px', borderRadius: 6, fontSize: 13, fontWeight: active ? 700 : 400,
        border: active ? '1px solid #6366f1' : '1px solid #e0e0e0',
        background: active ? '#ede9fe' : '#fff',
        color: active ? '#6366f1' : disabled ? '#dadce0' : '#3c4043',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {label}
    </button>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  root: {
    display: 'flex', height: '100%', minHeight: '80vh',
    fontFamily: 'Google Sans, Roboto, Arial, sans-serif',
    background: '#f6f8fc',
  } as React.CSSProperties,

  sidebar: {
    width: 220, flexShrink: 0,
    padding: '8px 0',
    display: 'flex', flexDirection: 'column' as const, gap: 1,
    overflowY: 'auto' as const,
  } as React.CSSProperties,

  sidebarItem: (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '8px 16px 8px 24px',
    borderRadius: '0 24px 24px 0',
    fontSize: 14, fontWeight: active ? 700 : 400,
    color: active ? '#202124' : '#444746',
    background: active ? '#d3e3fd' : 'transparent',
    cursor: 'pointer',
    userSelect: 'none',
  }),

  sideSection: {
    marginTop: 4,
  } as React.CSSProperties,

  sideSectionHeader: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 16px 8px 24px',
    fontSize: 14, fontWeight: 600,
    color: '#444746',
    cursor: 'pointer',
    userSelect: 'none' as const,
    borderRadius: '0 24px 24px 0',
  } as React.CSSProperties,

  badge: {
    marginLeft: 'auto', fontSize: 11, fontWeight: 700,
    color: '#202124',
  } as React.CSSProperties,

  main: {
    flex: 1, background: '#fff',
    borderRadius: 16, margin: '8px 16px 8px 0',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    display: 'flex', flexDirection: 'column' as const,
    overflow: 'hidden',
  } as React.CSSProperties,

  toolbar: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 16px',
    minHeight: 52,
  } as React.CSSProperties,

  toolbarLeft: {
    display: 'flex', alignItems: 'center', gap: 4,
  } as React.CSSProperties,

  toolbarRight: {
    display: 'flex', alignItems: 'center', gap: 4,
  } as React.CSSProperties,

  checkWrapper: {
    display: 'flex', alignItems: 'center', gap: 2,
    cursor: 'pointer', padding: '0 6px',
  } as React.CSSProperties,

  check: {
    width: 16, height: 16, accentColor: '#6366f1', cursor: 'pointer',
  } as React.CSSProperties,

  pageInfo: {
    fontSize: 13, color: '#444746', marginRight: 4,
  } as React.CSSProperties,

  divider: {
    height: 1, background: '#f1f3f4',
  } as React.CSSProperties,

  loadingBar: {
    height: 3,
    background: 'linear-gradient(90deg, #6366f1 0%, #818cf8 50%, #6366f1 100%)',
    backgroundSize: '200% 100%',
    animation: 'loading-bar 1.2s linear infinite',
  } as React.CSSProperties,

  list: {
    flex: 1, overflowY: 'auto' as const,
  },

  empty: {
    display: 'flex', flexDirection: 'column' as const,
    alignItems: 'center', justifyContent: 'center',
    padding: '80px 24px',
  },

  row: (read: boolean, sel: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'flex-start', gap: 12,
    padding: '8px 16px',
    background: sel ? '#e8f0fe' : read ? '#fff' : '#f2f6fc',
    borderBottom: '1px solid #f1f3f4',
    cursor: 'default',
    transition: 'background 0.1s',
  }),

  rowLeft: {
    display: 'flex', alignItems: 'center',
    paddingTop: 6,
  } as React.CSSProperties,

  rowContent: {
    flex: 1, minWidth: 0, cursor: 'pointer',
  } as React.CSSProperties,

  rowTop: {
    display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' as const,
  },

  sender: (unread: boolean): React.CSSProperties => ({
    fontSize: 14, fontWeight: unread ? 700 : 500,
    color: '#202124', whiteSpace: 'nowrap', minWidth: 120,
  }),

  subject: (unread: boolean): React.CSSProperties => ({
    fontSize: 14, fontWeight: unread ? 600 : 400,
    color: '#202124', flex: 1, minWidth: 0,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
  }),

  snippet: {
    fontSize: 13, color: '#5f6368',
    overflow: 'hidden', textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const, marginTop: 2,
  } as React.CSSProperties,

  tags: {
    display: 'flex', gap: 4, flexWrap: 'wrap' as const, alignItems: 'center',
  },

  date: (unread: boolean): React.CSSProperties => ({
    fontSize: 12, fontWeight: unread ? 700 : 400,
    color: '#5f6368', whiteSpace: 'nowrap', marginLeft: 'auto',
  }),

  typeBadge: (bg: string, color: string): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '1px 7px', borderRadius: 10,
    fontSize: 10, fontWeight: 600,
    background: bg, color,
  }),

  expandBtn: {
    display: 'flex', alignItems: 'center', paddingTop: 8,
    cursor: 'pointer', flexShrink: 0,
  } as React.CSSProperties,

  expandedBody: {
    marginTop: 12,
    padding: '20px 20px',
    background: '#f8fafc',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
  } as React.CSSProperties,

  detailLoading: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0',
  } as React.CSSProperties,

  spinner: {
    width: 18, height: 18, borderRadius: '50%',
    border: '2px solid #e2e8f0',
    borderTopColor: '#6366f1',
    animation: 'spin 0.7s linear infinite',
    flexShrink: 0,
  } as React.CSSProperties,

  bottomPager: {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10,
    padding: '10px 16px',
    borderTop: '1px solid #f1f3f4',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,
};

export default Inbox;
